import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import { useAuth } from '../../shared/context/AuthContext'
import { useCreateAmbassadorDiscountCouponMutation } from './hooks/useDiscountCouponQuery'
import { VALID_FOR_OPTIONS } from './discountCouponConstants'

/**
 * Port of `app/discountCoupon/ambassadorDiscountCouponsAdd.html` +
 * `app/discountCoupon/ambassadorDiscountCouponsAddController.js` (AngularJS
 * `ambassadorDiscountCouponsAddController`, 133 lines) — the "Adicionar
 * Campanha Embaixadores" screen, reached at
 * `/workspace/discount-coupons/add/ambassador` (legacy state
 * `workspace.ambassadorDiscountCouponsAdd`, see `AppRoutes.jsx`). Task 033
 * scope (one of the 3 specialized "add coupon" variants grouped with
 * `AutomaticDiscountCouponAddPage.jsx`/`ComboDiscountCouponAddPage.jsx`,
 * distinct from the "standard" screen ported by task 032).
 *
 * Fields, 1:1 with the legacy template:
 * - `code` (text, required) — "Prefixo do cupom".
 * - `integrationCode` (text, required) — "Código da Integração".
 * - `validFor` (select, defaults `ONE_MONTH`, same 6 options/labels as
 *   `DiscountCouponsListPage.jsx`'s `VALID_FOR_LABELS`) — "Válido a partir
 *   da aplicação por".
 * - One discount-value number input per plan in `$scope.plans` (8 plans:
 *   Up/Smart/Company/Vip x Mensal/Anual) — "Valor de desconto".
 * - `partnerName` (text, required) — "Nome do Embaixador / Campanha".
 * - `vendor` (text, required, defaulted to the logged-in user's login via
 *   `ng-init="discountCoupon.vendor = $parent.userLogin"`) — "Quem criou o
 *   cupom".
 * - `observation` (multiline text, optional) — "Observações".
 *
 * No `fromDate`/`dueDate`/`category` fields here (unlike the automatic
 * variant) — confirmed absent from `ambassadorDiscountCouponsAdd.html`.
 *
 * `$scope.plans.forEach(({ key }) => { if (!!$scope.discountCoupon)
 * $scope.discountCoupon.discountValues[key].discount = 0; })` runs at
 * controller-construction time, *before* the template's `ng-init="onInit()"`
 * has set `$scope.discountCoupon = {}` — so `$scope.discountCoupon` is
 * `undefined` at that point, the `if (!!$scope.discountCoupon)` guard is
 * `false`, and the loop body never executes (confirmed by reading execution
 * order: AngularJS runs the controller function fully before linking/
 * `ng-init` on child elements). Each discount field therefore starts
 * genuinely empty (not `0`) and gets its `discountValues[key]` object
 * created lazily by `ng-model`'s two-way binding as the user types — ported
 * here as plain empty-string-backed `TextField`s (`discountValues` state
 * object built up only for keys the user actually fills in), same
 * observable behavior.
 *
 * `save(discountCoupon)`:
 * - `buildPlansDiscountsData`: for every `discountValues[key]` entry with a
 *   truthy `.discount`, splits `key` on `-` into `plan`/`recurrencyType` and
 *   emits `{ plan, recurrencyType, value: discount }` (no
 *   `minimumUpgrade`/`minimumValueUpgrade` field here, unlike the automatic
 *   variant — ambassador coupons have no upgrade-minimum concept).
 * - `buildAutomaticCouponsPayload` (legacy's own name, reused for the
 *   ambassador payload too): `{ observation, discountCouponCodePrefix: code,
 *   validFor, integrationPrefixCode: integrationCode, partnerName, discounts
 *   }`.
 * - `discountCoupon.userViewerLogin = AuthService.getUserLogin()` is set
 *   before building the log payload (`setDiscountCupomLogInfo`), then
 *   `DiscountCouponsService.saveAmbassadorCoupons(couponsPayload)` +
 *   `saveStatusLog(logPayload)` fire in that order (log built from the raw
 *   `discountCoupon` + `couponsPayload`, not chained on the save's
 *   response) — ported as `useCreateAmbassadorDiscountCouponMutation`
 *   (`useDiscountCouponQuery.js`), which sequences both requests the same
 *   way.
 * - Success: `Materialize.toast('Cupons de desconto criados com sucesso!',
 *   5000)` + `$state.go('workspace.discountCoupons')` -> snackbar + navigate
 *   back to the list on `onSuccess`.
 * - Error: `Materialize.toast('Erro ao cadastrar os cupons de desconto: ' +
 *   error.data?.errorMessage, 6000)` -> snackbar with the same message
 *   shape on `onError`.
 *
 * Cancel button (`ui-sref="workspace.discountCoupons"`) -> navigate to
 * `/workspace/discount-coupons`. Submit disabled while
 * `code`/`integrationCode`/`partnerName`/`vendor` are empty, mirroring
 * `ng-disabled="myform.$invalid"` with the template's `required` attributes
 * (no client-side format validation beyond "non-empty" in the legacy
 * template — plain text inputs, no `type="email"`/pattern).
 *
 * Materialize `pickadate`/`material_select` jQuery init in the inline
 * `<script>` is presentational chrome with no ported equivalent needed
 * (native MUI `Select`), same precedent as `DiscountCouponsListPage.jsx`.
 */

/** Port of `$scope.plans` in `ambassadorDiscountCouponsAddController.js`. */
const PLANS = [
  { key: 'UP-MONTHLY', tag: 'Up Mensal' },
  { key: 'UP-YEARLY', tag: 'Up Anual' },
  { key: 'SMART-MONTHLY', tag: 'Smart Mensal' },
  { key: 'SMART-YEARLY', tag: 'Smart Anual' },
  { key: 'COMPANY-MONTHLY', tag: 'Company Mensal' },
  { key: 'COMPANY-YEARLY', tag: 'Company Anual' },
  { key: 'VIP-MONTHLY', tag: 'Vip Mensal' },
  { key: 'VIP-YEARLY', tag: 'Vip Anual' },
]

/** Port of `buildPlansDiscountsData({ discountValues })`. */
function buildPlansDiscountsData(discountValues) {
  const allDiscounts = []

  Object.entries(discountValues).forEach(([key, discount]) => {
    if (!discount) return

    const [plan, recurrencyType] = key.split('-')
    allDiscounts.push({ plan, recurrencyType, value: discount })
  })

  return allDiscounts
}

/** Port of `buildAutomaticCouponsPayload(discountCoupon)` (ambassador variant). */
function buildAmbassadorCouponsPayload(form) {
  return {
    observation: form.observation ?? '',
    discountCouponCodePrefix: form.code,
    validFor: form.validFor,
    integrationPrefixCode: form.integrationCode,
    partnerName: form.partnerName,
    discounts: buildPlansDiscountsData(form.discountValues),
  }
}

function AmbassadorDiscountCouponAddPage() {
  const navigate = useNavigate()
  const { getUserName } = useAuth()
  const createMutation = useCreateAmbassadorDiscountCouponMutation()

  const [code, setCode] = useState('')
  const [integrationCode, setIntegrationCode] = useState('')
  const [validFor, setValidFor] = useState('ONE_MONTH')
  const [discountValues, setDiscountValues] = useState({})
  const [partnerName, setPartnerName] = useState('')
  const [vendor, setVendor] = useState(getUserName() || '')
  const [observation, setObservation] = useState('')
  const [snackbar, setSnackbar] = useState({ open: false, message: '' })

  function notify(message) {
    setSnackbar({ open: true, message })
  }

  function handleSnackbarClose() {
    setSnackbar((current) => ({ ...current, open: false }))
  }

  function handleDiscountChange(key, value) {
    setDiscountValues((current) => ({ ...current, [key]: value === '' ? undefined : Number(value) }))
  }

  const isValid = code.trim() !== '' && integrationCode.trim() !== '' && partnerName.trim() !== '' && vendor.trim() !== ''

  /** Port of `$scope.save(discountCoupon)`. */
  function handleSave() {
    const form = { code, integrationCode, validFor, discountValues, partnerName, vendor, observation }
    const couponsPayload = buildAmbassadorCouponsPayload(form)

    createMutation.mutate(couponsPayload, {
      onSuccess: () => {
        notify('Cupons de desconto criados com sucesso!')
        navigate('/workspace/discount-coupons')
      },
      onError: (error) =>
        notify(`Erro ao cadastrar os cupons de desconto: ${error?.response?.data?.errorMessage || error?.message || ''}`),
    })
  }

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Adicionar Campanha Embaixadores
      </Typography>

      <Paper sx={{ p: 3 }} variant="outlined">
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              id="code"
              label="Prefixo do cupom*"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              fullWidth
              required
            />
          </Grid>
          <Grid size={12}>
            <TextField
              id="integrationCode"
              label="Código da Integração*"
              value={integrationCode}
              onChange={(event) => setIntegrationCode(event.target.value)}
              fullWidth
              required
            />
          </Grid>
          <Grid size={12}>
            <TextField
              id="validFor"
              select
              label="Válido a partir da aplicação por*"
              value={validFor}
              onChange={(event) => setValidFor(event.target.value)}
              fullWidth
            >
              {VALID_FOR_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {PLANS.map((plan) => (
            <Grid key={plan.key} size={12} container spacing={2} sx={{ alignItems: 'center' }}>
              <Grid size={4}>
                <Typography variant="body2">
                  <strong>{plan.tag}</strong>
                </Typography>
              </Grid>
              <Grid size={8}>
                <TextField
                  id={plan.key}
                  type="number"
                  label="Valor de desconto"
                  value={discountValues[plan.key] ?? ''}
                  onChange={(event) => handleDiscountChange(plan.key, event.target.value)}
                  inputProps={{ min: 1, step: 'any' }}
                  fullWidth
                />
              </Grid>
            </Grid>
          ))}

          <Grid size={12}>
            <TextField
              id="partnerName"
              label="Nome do Embaixador / Campanha*"
              value={partnerName}
              onChange={(event) => setPartnerName(event.target.value)}
              fullWidth
              required
            />
          </Grid>
          <Grid size={12}>
            <TextField
              id="vendor"
              label="Quem criou o cupom*"
              value={vendor}
              onChange={(event) => setVendor(event.target.value)}
              fullWidth
              required
            />
          </Grid>
          <Grid size={12}>
            <TextField
              id="observation"
              label="Observações"
              value={observation}
              onChange={(event) => setObservation(event.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
          </Grid>

          <Grid size={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={() => navigate('/workspace/discount-coupons')}>Cancelar</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!isValid || createMutation.isPending}
            >
              Salvar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="info" variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default AmbassadorDiscountCouponAddPage
